import { zodResolver } from '@hookform/resolvers/zod';
import {
  ApiError,
  type CatalogueItemResource,
  type ProductCodeMappingResource,
  ProductCodesController_map_v1,
  type ProductCodesController_map_v1Body,
} from '@repo/api-client';
import { Alert, AlertDescription } from '@repo/ui/components/alert';
import { Button } from '@repo/ui/components/button';
import {
  Dialog,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@repo/ui/components/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@repo/ui/components/form';
import { Input } from '@repo/ui/components/input';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@repo/ui/components/select';
import { IconAlertTriangle } from '@tabler/icons-react';
import { useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { useApiClient } from '@/api/api-client';
import { messageForFailure } from '@/api/failure';
import { IDEMPOTENCY_KEY_HEADER } from '@/lib/idempotency-key';
import { attemptFor, type WriteAttempt } from '@/lib/write-attempt';

import { fieldLabel, fieldSurface, FormDialogContent } from '../auth/AuthFrame';

/**
 * Mapping one product code: `PUT /v1/billing-machines/{id}/product-codes/{code}`,
 * owner and manager. Answers `200` — a re-map replaces the mapping in force, and
 * the old one stays in the history.
 *
 * **The printed name must match the print-out.** An upload whose bill prints a
 * different name for this code is refused as `MACHINE_PRODUCT_CODE_REPROGRAMMED`,
 * so the field says so before that refusal has to. Nothing here changes its
 * case or spacing beyond the trim the API does itself.
 *
 * `MACHINE_PRODUCT_ITEM_RETIRED` lands on the item picker; the picker offers no
 * retired item, so it only arrives when one is retired while this form is open.
 */

/** What opens the form: blank, prefilled from an upload's refusal, or a re-map. */
export interface MappingDraft {
  readonly code: string;
  readonly name: string;
  /** The code is fixed: this replaces the mapping in force for it. */
  readonly remapping?: boolean;
}

const CODE_REQUIRED = 'Enter the product code the machine prints.';
const CODE_MALFORMED = 'A product code is 1 to 16 letters or digits, as the machine prints it.';
const NAME_REQUIRED = 'Enter the product name exactly as the machine prints it.';
const NAME_TOO_LONG = 'A printed name is at most 200 characters.';
const ITEM_REQUIRED = 'Choose the catalogue item this code sells.';

const mappingSchema = z.object({
  productCode: z
    .string()
    .trim()
    .min(1, CODE_REQUIRED)
    .regex(/^[A-Za-z0-9]{1,16}$/, CODE_MALFORMED),
  machineProductName: z.string().trim().min(1, NAME_REQUIRED).max(200, NAME_TOO_LONG),
  catalogueItemId: z.string().min(1, ITEM_REQUIRED),
});

type MappingValues = z.infer<typeof mappingSchema>;

export function MapProductCodeDialog({
  machineId,
  draft,
  items,
  itemsFailed,
  onClose,
  onMapped,
}: {
  machineId: string;
  /** What to open with, or `null` when the dialog is shut. */
  draft: MappingDraft | null;
  /** The catalogue items still on sale; `null` while they load. */
  items: readonly CatalogueItemResource[] | null;
  itemsFailed: boolean;
  onClose: () => void;
  onMapped: (mapping: ProductCodeMappingResource) => void;
}) {
  return (
    <Dialog
      open={draft !== null}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <FormDialogContent>
        <DialogHeader>
          <DialogTitle>{draft?.remapping === true ? 'Re-map code' : 'Map a code'}</DialogTitle>
          <DialogDescription>
            Which catalogue item a product code on this machine sells. Bills using the code are
            booked as that item.
          </DialogDescription>
        </DialogHeader>

        {draft !== null && (
          <MappingForm
            machineId={machineId}
            draft={draft}
            items={items}
            itemsFailed={itemsFailed}
            onMapped={onMapped}
          />
        )}
      </FormDialogContent>
    </Dialog>
  );
}

function MappingForm({
  machineId,
  draft,
  items,
  itemsFailed,
  onMapped,
}: {
  machineId: string;
  draft: MappingDraft;
  items: readonly CatalogueItemResource[] | null;
  itemsFailed: boolean;
  onMapped: (mapping: ProductCodeMappingResource) => void;
}) {
  const client = useApiClient();

  const form = useForm({
    resolver: zodResolver(mappingSchema),
    mode: 'onTouched',
    defaultValues: {
      productCode: draft.code,
      machineProductName: draft.name,
      catalogueItemId: '',
    },
  });

  const [formError, setFormError] = useState<string | null>(null);

  // Held while the body stands, fresh once it changes. See `lib/write-attempt.ts`.
  const attempt = useRef<WriteAttempt | null>(null);

  const pending = form.formState.isSubmitting;

  const itemOptions = useMemo(
    () => (items ?? []).map((item) => ({ label: `${item.name} · ${item.code}`, value: item.id })),
    [items],
  );

  const handleSubmit = async ({
    productCode,
    machineProductName,
    catalogueItemId,
  }: MappingValues) => {
    if (pending) return;

    setFormError(null);

    try {
      const body: ProductCodesController_map_v1Body = { catalogueItemId, machineProductName };
      const sending = attemptFor(attempt.current, JSON.stringify([productCode, body]));
      attempt.current = sending;

      onMapped(
        await client.request(ProductCodesController_map_v1, {
          path: { id: machineId, code: productCode },
          body,
          headers: { [IDEMPOTENCY_KEY_HEADER]: sending.key },
        }),
      );
    } catch (error) {
      if (error instanceof ApiError && error.code === 'MACHINE_PRODUCT_ITEM_RETIRED') {
        form.setError('catalogueItemId', { message: messageForFailure(error) });
      } else {
        setFormError(messageForFailure(error));
      }
    }
  };

  return (
    <Form {...form}>
      <form
        className="space-y-5"
        noValidate
        onSubmit={(event) => void form.handleSubmit(handleSubmit)(event)}
      >
        <FormField
          control={form.control}
          name="productCode"
          render={({ field }) => (
            <FormItem>
              <FormLabel required className={fieldLabel}>
                Product code
              </FormLabel>
              <FormControl
                render={
                  <Input
                    // Text, never a number: `0001` is the code, not `1`.
                    type="text"
                    className={`${fieldSurface} h-11 font-mono tracking-wider numeric`}
                    required
                    readOnly={draft.remapping === true}
                    placeholder="0001"
                    autoComplete="off"
                    spellCheck={false}
                    {...field}
                  />
                }
              />
              <FormDescription>
                {draft.remapping === true
                  ? 'Re-mapping replaces the mapping in force for this code. The old one stays in the history.'
                  : 'As the machine prints it on the bill, leading zeros included.'}
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="machineProductName"
          render={({ field }) => (
            <FormItem>
              <FormLabel required className={fieldLabel}>
                Printed name
              </FormLabel>
              <FormControl
                render={
                  <Input
                    className={`${fieldSurface} h-11 font-mono`}
                    required
                    placeholder="SINGLE RAINBOW SLIDE"
                    autoComplete="off"
                    spellCheck={false}
                    {...field}
                  />
                }
              />
              <FormDescription>
                Exactly as the machine prints it. A bill that prints a different name for this code
                is refused on upload, because the machine may have been reprogrammed.
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="catalogueItemId"
          render={({ field, fieldState }) => (
            <FormItem>
              <FormLabel required className={fieldLabel}>
                Catalogue item
              </FormLabel>
              <Select
                value={field.value}
                items={itemOptions}
                onValueChange={(next) => field.onChange(next ?? '')}
                disabled={items === null || items.length === 0}
              >
                <SelectTrigger
                  id="mapping-item"
                  aria-label="Catalogue item"
                  aria-invalid={fieldState.error !== undefined}
                  aria-describedby={
                    fieldState.error === undefined ? undefined : 'mapping-item-error'
                  }
                  className={`${fieldSurface} h-11 w-full`}
                >
                  <SelectValue
                    placeholder={
                      itemsFailed
                        ? 'The catalogue could not be loaded'
                        : items === null
                          ? 'Loading the catalogue…'
                          : 'Choose an item'
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  {/* Retired items are not offered: mapping to one is refused. */}
                  <SelectGroup>
                    {itemOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
              <FormMessage id="mapping-item-error" />
            </FormItem>
          )}
        />

        {formError !== null && (
          <Alert variant="destructive">
            <IconAlertTriangle />
            <AlertDescription>{formError}</AlertDescription>
          </Alert>
        )}

        <DialogFooter>
          <Button type="submit" disabled={pending || items === null}>
            {pending ? 'Saving…' : 'Save mapping'}
          </Button>
        </DialogFooter>
      </form>
    </Form>
  );
}
